import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';const TimeContext = createContext(null);
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;


export const useTime = () => {
  const ctx = useContext(TimeContext);
  if (!ctx) throw new Error('useTime must be used inside <TimeProvider>');
  return ctx;
};

/**
 * Single source of truth for the current clock state.
 * Both the top-bar widget and the Timesheet page consume this.
 * Auto-refreshes on window focus and every 30s.
 */
export const TimeProvider = ({ children }) => {
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/time/current`, { withCredentials: true });
      if (mountedRef.current) setCurrent(r.data);
    } catch (e) { /* user not logged in or offline — ignore */ }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const interval = setInterval(refresh, 30000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const clockIn = useCallback(async (outletId) => {
    if (!outletId) {
      toast.error('Select an outlet first');
      return false;
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/time/clock-in`, { outlet_id: outletId }, { withCredentials: true });
      toast.success('Clocked in');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clock-in failed');
      return false;
    } finally {
      await refresh(); // self-heal on success AND failure
      setBusy(false);
    }
  }, [refresh]);

  const clockOut = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/time/clock-out`, {}, { withCredentials: true });
      const mins = r.data?.total_minutes || 0;
      toast.success(`Clocked out — ${Math.floor(mins / 60)}h ${mins % 60}m`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clock-out failed');
      return false;
    } finally {
      await refresh();
      setBusy(false);
    }
  }, [refresh]);

  const value = { current, busy, refresh, clockIn, clockOut };
  return <TimeContext.Provider value={value}>{children}</TimeContext.Provider>;
};
